/**
 * Value-Based Gift With Purchase (GWP) Manager
 * Handles automatic addition/removal of gift items based on cart value thresholds
 */

class ValueGWPManager {
  constructor() {
    this.initialized = false;
    this.updateTimeout = null;
    this.isProcessing = false;
    this.lastCartState = {
      total: 0,
      giftInCart: false
    };
    
    // Bind methods
    this.updateBanner = this.updateBanner.bind(this);
    this.updateBannerAfterCartRefresh = this.updateBannerAfterCartRefresh.bind(this);
    this.debouncedUpdate = this.debouncedUpdate.bind(this);
    this.addGiftToCart = this.addGiftToCart.bind(this);
    this.removeGiftFromCart = this.removeGiftFromCart.bind(this);
  }

  /**
   * Initialize the Value GWP system
   */
  init() {
    if (this.initialized) {
      console.log('[Value GWP] Already initialized');
      return;
    }

    // Check if value GWP is enabled
    if (!window.theme || !window.theme.enableValueGWP) {
      console.log('[Value GWP] Not enabled or theme not loaded');
      return;
    }

    console.log('[Value GWP] Initializing...');

    // Make master check function available globally
    window.updateValueGwpBanner = this.updateBanner;
    window.checkValueGwpStatus = this.masterCheck.bind(this);

    // Initial update after a short delay to ensure DOM is ready
    setTimeout(() => {
      if (document.querySelector('[js-value-gwp-banner]')) {
        // Use updateBanner to check if gift needs to be added on page load
        this.updateBanner();
      }
    }, 150);

    // Subscribe to cart updates with debouncing
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      theme.utils.subscriptions.subscribe(
        window.PUB_SUB_EVENTS.cartUpdate,
        this.debouncedUpdate
      );
    }

    // Listen for cart drawer open event
    this.setupCartDrawerListener();

    this.initialized = true;
    console.log('[Value GWP] Initialized successfully');
  }

  /**
   * Setup listener for cart drawer open event
   */
  setupCartDrawerListener() {
    const cartDrawer = document.querySelector('#CartDrawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('open', () => {
        console.log('[Value GWP] Cart drawer opened, checking cart state');
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          // Use updateBanner which checks if gift needs to be added/removed
          // Then updates display accordingly
          this.updateBanner();
        }, 50);
      });
      console.log('[Value GWP] Cart drawer listener attached');
    } else {
      console.log('[Value GWP] Cart drawer element not found');
    }
  }

  /**
   * Master check function - checks cart status and updates banner display
   * This is the single source of truth for banner state
   * Called on: page load, cart drawer open, cart updates
   */
  masterCheck() {
    const banner = document.querySelector('[js-value-gwp-banner]');
    
    if (!banner) {
      console.log('[Value GWP] Master check: banner not found');
      return;
    }

    console.log('[Value GWP] Running master check...');

    // Fetch current cart state
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        const message = document.querySelector('[js-value-gwp-message]');
        const progress = document.querySelector('[js-value-gwp-progress]');

        if (!message || !progress) {
          console.log('[Value GWP] Master check: message or progress elements not found');
          return;
        }

        const cartTotal = cart.total_price;
        const cartCurrency = cart.currency || 'USD';
        const threshold = window.theme.valueGwpThresholds[cartCurrency] || window.theme.valueGwpThreshold;
        console.log('[Value GWP] Master check: threshold in '+cartCurrency+' is', threshold);

        // Check if gift is in cart
        const giftInCart = cart.items.some(item =>
          item.variant_id === window.theme.valueGwpVariantId
        );

        // Calculate cart total excluding the gift item
        let cartTotalWithoutGift = cartTotal;
        if (giftInCart) {
          const giftItem = cart.items.find(item => item.variant_id === window.theme.valueGwpVariantId);
          if (giftItem) {
            cartTotalWithoutGift = cartTotal - (giftItem.final_line_price || 0);
          }
        }

        const shouldHaveGift = cartTotalWithoutGift >= threshold;
        const difference = threshold - cartTotalWithoutGift;
        const percentage = Math.min(100, (cartTotalWithoutGift / threshold) * 100);

        console.log('[Value GWP] Master check:', {
          cartTotal: cartTotalWithoutGift,
          threshold,
          shouldHaveGift,
          giftInCart,
          percentage: percentage.toFixed(1) + '%'
        });

        // Update progress bar
        progress.value = percentage;

        // Update message based on current state
        if (shouldHaveGift || giftInCart) {
          let successMsg = window.theme.valueGwpMessages.success;
          successMsg = successMsg.replace('{product}', window.theme.valueGwpProductTitle);
          message.innerHTML = successMsg;
          //message.classList.add('font-700');
          console.log('[Value GWP] Master check: showing success message');
        } else {
          const differenceFormatted = theme.utils.formatMoney(difference, theme.config.money.format);
          let upsellMsg = window.theme.valueGwpMessages.upsell;
          upsellMsg = upsellMsg.replace('{amount}', differenceFormatted);
          upsellMsg = upsellMsg.replace('{product}', window.theme.valueGwpProductTitle);
          message.innerHTML = upsellMsg;
          //message.classList.remove('font-700');
          console.log('[Value GWP] Master check: showing upsell message');
        }

        // Store the current state
        this.lastCartState = {
          total: cartTotalWithoutGift,
          giftInCart: giftInCart
        };
      })
      .catch(error => {
        console.error('[Value GWP] Master check error:', error);
      });
  }

  /**
   * Debounced update to prevent multiple rapid calls
   */
  debouncedUpdate() {
    clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(() => {
      this.updateBanner();
    }, 100);
  }

  /**
   * Add gift item to cart
   */
  addGiftToCart() {
    if (this.isProcessing) {
      console.log('[Value GWP] Already processing, skipping add');
      return;
    }
    
    // Check if product has been marked as unavailable
    const banner = document.querySelector('[js-value-gwp-banner]');
    if (banner && banner.getAttribute('data-unavailable') === 'true') {
      console.log('[Value GWP] Product marked as unavailable, skipping add');
      return;
    }

    this.isProcessing = true;
    console.log('[Value GWP] Adding gift to cart:', window.theme.valueGwpVariantId);

    // Get cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');
    
    const formData = {
      items: [{
        id: window.theme.valueGwpVariantId,
        quantity: 1,
        properties: {
          '_gift': 'Value GWP',
          '_gift_type': 'value_gwp'
        }
      }],
      sections: ['cart', 'cart-count']
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => {
      // Check if response is ok (status 200-299)
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    })
    .then(data => {
      // Check if Shopify returned an error in the JSON (sometimes 200 status but with error)
      if (data.status === 422 || data.status === 'error' || data.message) {
        console.error('[Value GWP] Shopify returned error:', data);
        this.handleProductUnavailable(data);
        this.isProcessing = false;
        return;
      }
      
      console.log('[Value GWP] Gift added successfully:', data);
      
      // Update cart drawer with new sections
      if (cartDrawer && data.sections) {
        cartDrawer.renderContents(data);
      }
      
      // Update cart bubble count
      if (window.updateMultipleCartBubbles) {
        window.updateMultipleCartBubbles();
      }
      
      // Reset processing flag BEFORE triggering updates
      this.isProcessing = false;
      
      // Trigger cart update event for other listeners
      if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        document.dispatchEvent(new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate));
      }
      
      // Explicitly update banner after DOM has been refreshed
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        this.updateBannerAfterCartRefresh();
      });
    })
    .catch(error => {
      console.error('[Value GWP] Error adding gift:', error);
      this.handleProductUnavailable(error);
      this.isProcessing = false;
    });
  }

  /**
   * Handle product unavailability by updating banner to show error message
   */
  handleProductUnavailable(error) {
    console.log('[Value GWP] Product is no longer available:', error);
    
    const banner = document.querySelector('[js-value-gwp-banner]');
    const message = document.querySelector('[js-value-gwp-message]');
    const progress = document.querySelector('[js-value-gwp-progress]');
    
    if (!banner || !message) {
      return;
    }
    
    // Update message to show offer is no longer available
    message.innerHTML = 'This offer is no longer available.';
    message.classList.remove('font-700');
    message.style.color = '#999'; // Gray out the text
    
    // Set progress to 0 and gray it out
    if (progress) {
      progress.value = 0;
      progress.style.opacity = '0.3';
    }
    
    // Add a flag to prevent further attempts
    banner.setAttribute('data-unavailable', 'true');
    
    console.log('[Value GWP] Banner updated with unavailable message');
  }

  /**
   * Remove gift item from cart
   */
  removeGiftFromCart(cart) {
    if (this.isProcessing) {
      console.log('[Value GWP] Already processing, skipping remove');
      return;
    }

    // Find the gift item in the cart
    const giftItem = cart.items.find(item =>
      item.variant_id === window.theme.valueGwpVariantId
    );

    if (!giftItem) {
      console.log('[Value GWP] Gift not found in cart');
      return;
    }

    this.isProcessing = true;
    console.log('[Value GWP] Removing gift from cart');

    // Get cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: giftItem.key,
        quantity: 0,
        sections: ['cart', 'cart-count']
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('[Value GWP] Gift removed successfully:', data);
      
      // Update cart drawer with new sections
      if (cartDrawer && data.sections) {
        cartDrawer.renderContents(data);
      }
      
      // Update cart bubble count
      if (window.updateMultipleCartBubbles) {
        window.updateMultipleCartBubbles();
      }
      
      // Reset processing flag BEFORE triggering updates
      this.isProcessing = false;
      
      // Trigger cart update event for other listeners
      if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        document.dispatchEvent(new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate));
      }
      
      // Explicitly update banner after DOM has been refreshed
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        this.updateBannerAfterCartRefresh();
      });
    })
    .catch(error => {
      console.error('[Value GWP] Error removing gift:', error);
      this.isProcessing = false;
    });
  }

  /**
   * Update banner display after cart refresh
   * Simply calls masterCheck to update the display
   */
  updateBannerAfterCartRefresh() {
    console.log('[Value GWP] Updating banner after cart refresh');
    this.masterCheck();
  }

  /**
   * Update the GWP banner display and handle add/remove operations
   * This checks if gift should be added/removed and triggers those operations
   * Then calls masterCheck to update the display
   */
  updateBanner() {
    const banner = document.querySelector('[js-value-gwp-banner]');

    if (!banner) {
      console.log('[Value GWP] updateBanner: Banner elements not found');
      return;
    }

    // Get cart data
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartTotal = cart.total_price;
        const cartCurrency = cart.currency || 'USD';

        // Get threshold for current currency
        const threshold = window.theme.valueGwpThresholds[cartCurrency] || window.theme.valueGwpThreshold;

        // Check if gift is in cart
        const giftInCart = cart.items.some(item =>
          item.variant_id === window.theme.valueGwpVariantId
        );

        // Calculate cart total excluding the gift item
        let cartTotalWithoutGift = cartTotal;
        if (giftInCart) {
          const giftItem = cart.items.find(item => item.variant_id === window.theme.valueGwpVariantId);
          if (giftItem) {
            cartTotalWithoutGift = cartTotal - (giftItem.final_line_price || 0);
          }
        }

        // Determine if we should add or remove the gift
        const shouldHaveGift = cartTotalWithoutGift >= threshold;
        
        console.log('[Value GWP] updateBanner decision:', {
          cartTotal: cartTotalWithoutGift,
          threshold,
          shouldHaveGift,
          giftInCart,
          isProcessing: this.isProcessing
        });

        // Check if product has been marked as unavailable
        const isUnavailable = banner.getAttribute('data-unavailable') === 'true';
        
        // Handle gift addition/removal if state changed
        if (shouldHaveGift && !giftInCart && !this.isProcessing && !isUnavailable) {
          // Qualified for gift, but don't have it - add it
          console.log('[Value GWP] ✓ Qualified for gift, calling addGiftToCart()');
          this.addGiftToCart();
          return; // addGiftToCart will call masterCheck after refresh
        } else if (!shouldHaveGift && giftInCart && !this.isProcessing) {
          // No longer qualified, but have gift - remove it
          console.log('[Value GWP] ✗ No longer qualified, calling removeGiftFromCart()');
          this.removeGiftFromCart(cart);
          return; // removeGiftFromCart will call masterCheck after refresh
        }

        // No add/remove needed, just update display
        console.log('[Value GWP] No action needed, updating display via masterCheck()');
        this.masterCheck();
      })
      .catch(error => {
        console.error('[Value GWP] Error in updateBanner:', error);
      });
  }
}

// Initialize on page load
(function() {
  const manager = new ValueGWPManager();
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      manager.init();
    });
  } else {
    manager.init();
  }

  // Make manager available globally for debugging
  window.valueGWPManager = manager;
})();
